import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { X } from "lucide-react";

interface PostImageProps {
    imageUrl: string;
    onImageChange: (newUrl: string) => void;
}

export default function PostImage({ imageUrl, onImageChange }: PostImageProps) {
    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = (event) => {
            if (event.target?.result) {
                onImageChange(event.target.result as string);
            }
        };
        reader.readAsDataURL(file);
    }, [onImageChange]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onImageChange("/images/placeholder.svg");
    };

    return (
        <div className="relative" {...getRootProps()}>
            <div {...getRootProps()} className="relative min-h-[60px]">
                {imageUrl ? (
                    <>
                        {imageUrl.startsWith("data:video") ? (
                            <video src={imageUrl} controls className="w-full h-auto" />
                        ) : (
                            <img src={imageUrl} alt="Post" className="w-full h-auto" />
                        )}
                        {imageUrl !== "/images/placeholder.svg" && (
                            <button
                                onClick={handleRemove}
                                className="absolute top-2 right-2 bg-black bg-opacity-50 rounded-full p-1"
                            >
                                <X className="h-4 w-4 text-white" />
                            </button>
                        )}
                    </>
                ) : null}
                <input {...getInputProps()} />
                {isDragActive && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-md">
                        <p className="text-white text-center">Drop the image here to replace</p>
                    </div>
                )}
                {!imageUrl && !isDragActive && (
                    <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center text-gray-300">
                        Drag and drop an image here, or click to select a file
                    </div>
                )}
            </div>
        </div>
    );
}
